import axiosInstance from "../../../utils/axiosConfig";
import React, { useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../../components/data-table";
import { API_URL } from "../../../constant";
import { useAuth } from "../../../context/auth-context";
import {
  buildSurveyResponseRows,
  createSurveyResponseColumns,
  SURVEY_TABLE_LIMIT,
  type SurveyResponseRow,
} from "./surveyResponsesTable";
import type { SurveyResponse } from "./fetchSurveyResponsesExport";

type ListTableProps = {
  list: number;
  searchTerm: string;
};

const ListTable: React.FC<ListTableProps> = ({ list, searchTerm }) => {
  const { isAuthenticated } = useAuth();
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);

    const fetchData = async () => {
      try {
        const searchParam = searchTerm ? `&search=${searchTerm}` : "";
        const { data } = await axiosInstance.get(
          `${API_URL}api/users/survey/responses?page=${page}&limit=${SURVEY_TABLE_LIMIT}${searchParam}`
        );
        setTotalPages(Math.max(1, data?.data?.meta?.totalPages ?? 1));
        setTotalUsers(data?.data?.meta?.totalUsers ?? 0);
        setResponses(
          Array.isArray(data?.data?.responses) ? data.data.responses : []
        );
      } catch (error: any) {
        console.log(error?.response?.data?.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, 600);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, page, isAuthenticated]);

  const columns = useMemo(() => createSurveyResponseColumns(), []);
  const rows = useMemo<SurveyResponseRow[]>(
    () => buildSurveyResponseRows(responses, list),
    [responses, list]
  );

  if (loading && responses.length === 0) {
    return (
      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  return (
    <div className="w-full pb-10">
      {loading && (
        <p className="mb-2 text-[12px] text-[#869AA9]">Updating list…</p>
      )}
      <DataTable<SurveyResponseRow>
        columns={columns}
        data={rows}
        currentPage={page}
        setCurrentPage={setPage}
        totalPage={totalPages}
        count={totalUsers}
        limit={SURVEY_TABLE_LIMIT}
        emptyContent="No survey responses to display."
        className="overflow-hidden rounded-xl border border-[#2C363E]"
        fixedBodyHeight="min(560px, 55vh)"
      />
    </div>
  );
};

export default ListTable;
